import { useCallback, useEffect, useRef } from 'react';

type ReleaseScrollLock = () => void;

const activeLocks = new Set<symbol>();
let previousOverflow: string | null = null;

/**
 * Reference-counted document scroll lock.
 *
 * All overlays lock <html> instead of <body> because body overflow locking
 * breaks position:fixed on iOS Safari. A lock is released only by its owner,
 * so closing one overlay cannot unlock another overlay that is still visible.
 */
export function acquireScrollLock(): ReleaseScrollLock {
  if (typeof document === 'undefined') return () => {};

  const token = Symbol('scroll-lock');
  if (activeLocks.size === 0) {
    previousOverflow = document.documentElement.style.overflow;
  }
  activeLocks.add(token);
  document.documentElement.style.overflow = 'hidden';

  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeLocks.delete(token);

    if (activeLocks.size === 0) {
      document.documentElement.style.overflow = previousOverflow ?? '';
      previousOverflow = null;
    }
  };
}

/**
 * Locks while `active` is true. Set releaseOnDeactivate=false for components
 * whose AnimatePresence keeps their overlay mounted during its exit animation;
 * call the returned release function from onExitComplete.
 */
export function useScrollLock(active: boolean, releaseOnDeactivate = true): ReleaseScrollLock {
  const releaseRef = useRef<ReleaseScrollLock | null>(null);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;

    if (active && !releaseRef.current) {
      releaseRef.current = acquireScrollLock();
    } else if (!active && releaseOnDeactivate) {
      releaseRef.current?.();
      releaseRef.current = null;
    }
  }, [active, releaseOnDeactivate]);

  useEffect(() => {
    return () => {
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, []);

  return useCallback(() => {
    if (!activeRef.current) {
      releaseRef.current?.();
      releaseRef.current = null;
    }
  }, []);
}